<script setup>
/**
 * A Card Database search result.
 *
 * When the user already owns this card, the response says so directly:
 * `belongs_to_batches` is built server-side from their cards carrying this
 * `collx_match_id`. So the row can show which batches it's in without a single
 * extra request — and unlike counting inventory hits, that answer covers every
 * copy, not just the ones on the current page.
 *
 * The `inventory` block is a separate result set (its own section); it is not
 * the source for these chips.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useSearchStore } from '@/stores/SearchStore'
import { useBatchStore } from '@/stores/BatchStore'
import { isBatchLockedForSet } from '@/helpers/searchDatabaseMatchField'
import { catalogBatches } from '@/helpers/globalSearch'
import { batchViewPath } from '@/helpers/batch'

const props = defineProps({
  active: { type: Boolean, default: false },
  item: { type: Object, required: true }
})

const router = useRouter()
const searchStore = useSearchStore()
const batchStore = useBatchStore()

const batches = computed(() => catalogBatches(props.item))

// Design-aware: `/inventory/batch/:id` in the new design, `/batches/:id` in the
// classic one.
const goToBatch = (batch) => {
  searchStore.cdp_search.open = false
  router.push(batchViewPath(batch.id))
}
</script>

<template>
  <li
    class="flex items-center gap-x-2 p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
    :class="{ 'bg-blue-50': props.active }"
  >
    <div class="w-10 h-10 relative flex-none flex items-center justify-center">
      <img :src="props.item.frontImageUrl" class="relative z-10 max-h-10" />
      <img
        :src="props.item.backImageUrl"
        class="absolute top-1/2 -translate-y-1/2"
        v-if="props.item.backImageUrl"
      />
    </div>
    <div class="flex flex-col flex-grow">
      <h3 class="font-medium mb-0.5">#{{ props.item.number }} {{ props.item.name ? props.item.name : props.item.player ? props.item.player.name : '' }}</h3>
      <p class="text-gray-500 mb-1.5">{{ props.item.set?.name }}</p>
      <div class="flex items-center flex-wrap gap-1 text-[10px] mt-auto">
        <span v-if="props.item.subset" class="badge secondary border border-white px-1.5 rounded-full">{{ props.item.subset.name }}</span>
        <span v-if="props.item.variation" class="badge secondary border border-white px-1.5 rounded-full">{{ props.item.variation.name }}</span>
        <span v-if="props.item.attributes" class="badge secondary border border-white px-1.5 rounded-full">{{ props.item.attributes.name }}</span>
      </div>

      <!-- Owned already: which batches, straight from the response.
           `.stop.prevent` because the row is a ComboboxOption — a click reaching
           it would select the option (add the card) instead of navigating. -->
      <div v-if="batches.length" class="flex items-center flex-wrap gap-1 text-[10px] mt-1.5">
        <span class="text-gray-400">In batches:</span>
        <button
          v-for="batch in batches"
          :key="batch.id"
          type="button"
          class="badge primary border border-white px-1.5 rounded-full hover:underline"
          @mousedown.stop.prevent
          @click.stop.prevent="goToBatch(batch)"
        >{{ batch.name }}</button>
      </div>
    </div>
    <!-- TODO: need to check if I need lock_includes_parallels or need to check at least one locked set has include_parallels -->
    <button
      class="
        alternative-button text-xs py-1 text-tint-1 focus:ring-tint-1/20 transition duration-300 lg:opacity-0
        group-hover:opacity-100
      "
      :class="{'!opacity-100': props.active}"
      :disabled="isBatchLockedForSet(batchStore.selectedBatch, props.item)"
    >
      <span v-if="searchStore.cdp_search.locationOfSearchCall === 'add-match'">Select Match</span>
      <span v-else>Add Card</span>
    </button>
  </li>
</template>
